import React, { useCallback, useState, forwardRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Card, ModalBody } from 'reactstrap';
import { When } from 'react-if';
import { useHistory } from 'react-router-dom';
import PostHeader from '../PostHeader';
import Fog from '../../../../components/elements/fog/Fog';
import PostBody from '../PostBody';
import './styles.css';
import PostFooter from '../PostFooter';
import { useAuth } from '../../../../hooks/useAuth';
import { useToggle } from 'react-use';
import CreateReviewForm from '../CreateReviewForm';
import { createReview } from '../../../../firebase/api/review';
import { markPostAsUnread, updatePost } from '../../../../firebase/api/post';
import { getUserByUid } from '../../../../firebase/api/users';
import { sendEmail } from '../../../../firebase/api/mail';
import {
    getAcceptPostTemplate,
    getDeclinePostTemplate,
    getNewReviewTemplate,
} from '../../../../templates';
import PostInviteModal from '../../../../components/elements/customModals/PostInviteModal/PostInviteModal';
import EmailAllModal from '../../../../components/elements/customModals/EmailAllModal';
import { DeletePostButton } from '../../../../components/post/elements/DeletePostButton/DeletePostButton';
import { EditPostButton } from '../../../../components/post/elements/EditPostButton/EditPostButton';
import DeletePostModal from '../../../../components/elements/modal/DeletePostModal';
import { ApproveDeclinePost } from '../../../../components/post/admin/ApproveDeclinePost';
import { convertDateToFirestoreTimestamp } from '../../../../firebase/api/firestoreUtils';
import { INVITE_ALL_USERS } from '../../../../constants/post';
import { useReviews } from '../../useReviews';
import ConfirmationModal from '../../../../components/elements/modal/NewConfirmModal';
import { ReactComponent as CloseAccountIcon } from '../../../../components/elements/modal/close-account-icon.svg';

const Post = forwardRef(
    (
        {
            id,
            index,
            title,
            measure,
            description,
            categoryId,
            createdTimestamp,
            firstName,
            lastName,
            totalDonations,
            publisherUid,
            media,
            questionType,
            questions,
            style,
            rawPost,
            isApprovedByAdmin,
            isPreview,
            isPostPopup,
            approvedMode,
            decreasePostCount,
            decreasePendingPostCount,
            unreadPostIds,
            invitationsPostIds,
            currentPostPage,
            viewInvitationPost,
            onReviewsOpen,
            onRefresh,
            onReviewButtonClick,
        },
        ref
    ) => {
        const {
            currentUser: { uid: userId },
        } = useAuth();
        const history = useHistory();

        const [isReviewOpens, toggleReviews] = useToggle(false);
        const [isReviewAddFormOpens, toggleReviewAddForm] = useToggle(false);
        const [isInviteFriendsModalOpens, toggleInviteFriendsModal] = useToggle(false);
        const [isEmailAllModalOpens, toggleEmailAllModal] = useToggle(false);
        const [isDeletePostModalOpens, toggleDeletePostModalOpens] = useToggle(false);

        const [selectedQuestions, setSelectedQuestions] = useState([]);

        const {
            isLoading: isReviewsLoading,
            needShowLoadMoreButton,
            count: reviewsCount,
            alreadyReviewed,
            onLastItemSet,
            reviews,
        } = useReviews(id, userId);

        useLayoutEffect(() => {
            let intervalId = setInterval(measure, 50);

            setTimeout(() => {
                clearInterval(intervalId);
            }, 500);
        }, [isReviewOpens, isReviewAddFormOpens, isReviewsLoading]);

        const isPostOwner = userId === publisherUid;

        const renderFogText = useCallback(() => {
            return (
                <>
                    <h6 className="font-weight-bold mr-4 card-info fog__text-header">
                        Congratulation on your post!
                    </h6>
                    <div className="fog__text">
                        {"It's currently in review, so once approved it will be live."}
                    </div>
                </>
            );
        }, []);

        const handleReviewAddFormButtonClick = useCallback(
            newStatus => () => {
                if (!isPreview) {
                    toggleReviewAddForm(newStatus);
                }
                onReviewButtonClick();
            },
            [isPreview, toggleReviewAddForm]
        );

        const handleReviewPublish = useCallback(
            async text => {
                const reviewDto = {
                    donation: 0,
                    minDonation: 0,
                    postOwnerUid: publisherUid,
                    postUid: id,
                    publisherUid: userId,
                    questions: selectedQuestions,
                    text,
                };

                const review = await createReview(reviewDto);

                selectedQuestions.forEach(q => {
                    rawPost.question.questions[q.order].reviews.push(review.id);
                });

                await updatePost(rawPost);

                const { email, firstName } = await getUserByUid(rawPost.publisherUid);
                sendEmail(getNewReviewTemplate(email, firstName, id));
                await markPostAsUnread(id);
                handleReviewAddFormButtonClick(false)();
                onRefresh();
            },
            [
                publisherUid,
                id,
                userId,
                selectedQuestions,
                rawPost,
                handleReviewAddFormButtonClick,
                onRefresh,
            ]
        );

        const handleReviewsOpens = useCallback(async () => {
            toggleReviews();
            onReviewsOpen(index);
            if (decreasePostCount) {
                await decreasePostCount(id);
            }
        }, [onReviewsOpen, index, id, toggleReviews, decreasePostCount]);

        const handleQuestionClick = useCallback(
            ({ reviews: _, ...question }, isSingleSelection) => {
                if (isSingleSelection) {
                    const newQuestions = isSingleSelection ? [question] : [];
                    setSelectedQuestions(newQuestions);
                    handleReviewAddFormButtonClick(true)();
                    return;
                }

                const isAlreadySelected = selectedQuestions.some(q => q.uid === question.uid);
                const newSelectedQuestions = isAlreadySelected
                    ? selectedQuestions.filter(q => q.uid !== question.uid)
                    : [...selectedQuestions, question];
                setSelectedQuestions(newSelectedQuestions);
            },
            [handleReviewAddFormButtonClick, selectedQuestions]
        );

        const handleInviteFriendsModalClose = useCallback(
            async inviteAllUsers => {
                if (inviteAllUsers) {
                    rawPost.inviteColleagues.push(INVITE_ALL_USERS);
                    await updatePost(rawPost);
                }

                toggleInviteFriendsModal(false);
            },
            [toggleInviteFriendsModal, rawPost]
        );

        const handleEditButtonClick = useCallback(() => {
            history.push(`/posts/edit/${rawPost.uid}`, rawPost);
        }, [history, rawPost]);

        const handleDeclineButtonClick = useCallback(
            async status => {
                const post = { ...rawPost, isDeleted: status };

                await updatePost(post);
                const { email, firstName } = await getUserByUid(post.publisherUid);
                sendEmail(getDeclinePostTemplate(email, firstName, id));
                decreasePendingPostCount();
                onRefresh();
            },
            [id, rawPost, onRefresh, decreasePendingPostCount]
        );

        const handleAcceptButtonClick = useCallback(
            async status => {
                const post = {
                    ...rawPost,
                    isApprovedByAdmin: status,
                    approvedByAdminDate: convertDateToFirestoreTimestamp(new Date()),
                };

                await updatePost(post);
                const { email, firstName } = await getUserByUid(rawPost.publisherUid);
                sendEmail(getAcceptPostTemplate(email, firstName, id));
                decreasePendingPostCount();
                onRefresh();
            },
            [rawPost, id, onRefresh, decreasePendingPostCount]
        );

        const handleDeleteButtonClick = useCallback(async () => {
            const post = { ...rawPost, isDeleted: true };
            await updatePost(post);
            onRefresh();
        }, [rawPost, onRefresh]);

        const handleLoadMoreReviewsButtonClick = useCallback(() => {
            onLastItemSet();
            onReviewsOpen();
        }, [onReviewsOpen, onLastItemSet]);

        return (
            <div
                className={classNames({
                    row: !isPostPopup,
                })}
                ref={ref}
                style={style}
            >
                <Card
                    className={classNames({
                        post__container: !isPostPopup,
                    })}
                >
                    <PostHeader
                        categoryId={categoryId}
                        createdTimestamp={createdTimestamp}
                        firstName={firstName}
                        lastName={lastName}
                        totalDonations={totalDonations}
                        reviewsCount={reviewsCount}
                        unreadPostIds={unreadPostIds}
                        invitationsPostIds={invitationsPostIds}
                        postId={id}
                        currentPostPage={currentPostPage}
                        viewInvitationPost={viewInvitationPost}
                    />
                    <Fog isVisible={!approvedMode && !isApprovedByAdmin} text={renderFogText}>
                        <PostBody
                            title={title}
                            description={description}
                            media={media}
                            questionType={questionType}
                            questions={questions}
                            onMultipleClick={handleQuestionClick}
                        />
                    </Fog>
                    <PostFooter
                        postId={id}
                        reviews={reviews}
                        count={reviewsCount}
                        questions={questions}
                        categoryId={categoryId}
                        questionType={questionType}
                        alreadyReviewed={alreadyReviewed}
                        isPostOwner={isPostOwner}
                        needShowLoadMoreButton={needShowLoadMoreButton}
                        measure={measure}
                        onClick={handleQuestionClick}
                        onRefresh={onRefresh}
                        onMultipleQuestionClick={handleReviewAddFormButtonClick(true)}
                        onReviewsOpen={handleReviewsOpens}
                        onEmailAllButtonClick={toggleEmailAllModal}
                        onInviteFriendsButtonClick={toggleInviteFriendsModal}
                        onLoadMoreReviewsButtonClick={handleLoadMoreReviewsButtonClick}
                    />
                    <When condition={isReviewAddFormOpens}>
                        {() => (
                            <CreateReviewForm
                                onPublish={handleReviewPublish}
                                onClose={handleReviewAddFormButtonClick(false)}
                            />
                        )}
                    </When>
                    <When condition={approvedMode && !isApprovedByAdmin}>
                        <ApproveDeclinePost
                            onDeclinePost={handleDeclineButtonClick}
                            onAcceptPost={handleAcceptButtonClick}
                        />
                    </When>
                    <div className="post__actions">
                        <When condition={!approvedMode && isPostOwner}>
                            <DeletePostButton onDeletePost={toggleDeletePostModalOpens} />
                        </When>
                        <When condition={!approvedMode && isPostOwner && !isApprovedByAdmin}>
                            <EditPostButton onEditPost={handleEditButtonClick} />
                        </When>
                    </div>
                </Card>
                <When condition={isInviteFriendsModalOpens}>
                    {() => (
                        <PostInviteModal
                            post={rawPost}
                            modal={isInviteFriendsModalOpens}
                            toggle={handleInviteFriendsModalClose}
                        />
                    )}
                </When>
                <When condition={isEmailAllModalOpens}>
                    {() => (
                        <EmailAllModal
                            isOpen={isEmailAllModalOpens}
                            postId={id}
                            onToggle={toggleEmailAllModal}
                        />
                    )}
                </When>
                <When condition={isDeletePostModalOpens}>
                    {() => (
                        <ConfirmationModal
                            renderIcon={() => <CloseAccountIcon />}
                            renderContent={() => (
                                <span> Are you sure you want to delete your post?</span>
                            )}
                            onConfirm={handleDeleteButtonClick}
                            onCancel={toggleDeletePostModalOpens}
                        />
                    )}
                </When>
            </div>
        );
    }
);

Post.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    style: PropTypes.object.isRequired,
    categoryId: PropTypes.string.isRequired,
    createdTimestamp: PropTypes.object.isRequired,
    totalDonations: PropTypes.number.isRequired,
    publisherUid: PropTypes.string.isRequired,
    rawPost: PropTypes.object.isRequired,
    approvedMode: PropTypes.bool,
    isApprovedByAdmin: PropTypes.bool,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    media: PropTypes.array,
    questionType: PropTypes.number,
    questions: PropTypes.array,
    onRefresh: PropTypes.func.isRequired,
    onReviewButtonClick: PropTypes.func,
    decreasePostCount: PropTypes.func,
    decreasePendingPostCount: PropTypes.func,
    isPreview: PropTypes.bool,
    isPostPopup: PropTypes.bool,
    currentPostPage: PropTypes.number.isRequired,
    allUnreadPostIds: PropTypes.array.isRequired,
    viewInvitationPost: PropTypes.func.isRequired,
};

Post.defaultProps = {
    isApprovedByAdmin: false,
    approvedMode: false,
    firstName: '',
    lastName: '',
    description: '',
    media: [],
    questionType: 0,
    questions: [],
    isPreview: false,
    isPostPopup: false,
    onReviewButtonClick: () => {},
};

export default React.memo(Post);
